import React, {
  forwardRef,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { capitalize, MediaType } from '@repo-level/core-front';

import { useComponentVisible } from 'hooks/visibility';
import { useTheme } from 'hooks/theme';
import {
  FieldStructure,
  FieldStructureProps,
} from '../structures/FieldStructure';
import { Responsive } from 'components/internal/Responsive';
import { ModalSelect } from 'components/ModalSelect';
import { ButtonCross } from 'components/buttons';
import { ScrollBlock } from 'components/ScrollBlock';

import {
  ScAnimatedPlaceholder,
  ScOptions,
  ScOptionsProps,
  ScSelectBlock,
  ScSelectButton,
  ScSelectMainProps,
  ScSelectSearch,
} from './styled';

export interface SelectOption {
  icon?: string;
  name: React.ReactNode;
  nameS?: string;
  nameV?: string;
  value: string;
  disabled?: boolean;
  image?: string;
}

type SelectButtonProps = FieldStructureProps &
  ScSelectMainProps & {
    selectedOption?: SelectOption;
    selected?: SelectOption['value'];
    defaultValue?: React.ReactNode;
    active?: boolean;
    handleOpen?: () => void;
    handleReset?: (e: any) => void;
    placeholder?: string;
    animatedPlaceholder?: string;
    canReset?: boolean;
    selectedOptionWithoutIcon?: boolean;
    noSelectioinIfOneOption?: boolean;
  };

export type SelectBlockProps = SelectButtonProps & {
  options: SelectOption[];
  onSelect?: (value: SelectOption['value']) => void;
  onOpen?: () => void;
  onClose?: () => void;
  native?: boolean;
  search?: boolean;
  isFilter?: boolean;
} & Pick<OptionsBlockProps, 'minOptionHeight'>;

export const SelectBlock: (
  props: SelectBlockProps & React.RefAttributes<HTMLDivElement>
) => React.ReactElement | null = forwardRef(
  (
    {
      placeholder,
      animatedPlaceholder,
      options,
      selected,
      error,
      hideErrorMessage,
      onSelect,
      disabled,
      defaultValue,
      onOpen,
      onClose,
      native,
      search,
      canReset,
      withBorderMode,
      noSelectioinIfOneOption,
      ...scProps
    }: SelectBlockProps,
    forwardedRef?: React.Ref<HTMLDivElement>
  ) => {
    const {
      ref,
      openerRef,
      isComponentVisible,
      setIsComponentVisible,
    } = useComponentVisible(false);

    const handleOpen = () => {
      if (!!disabled || (noSelectioinIfOneOption && options.length < 2)) return;

      setIsComponentVisible(!isComponentVisible);
    };

    const selectedOption = useMemo(
      () =>
        typeof selected === 'undefined'
          ? undefined
          : options.find(({ value }) => value === selected),
      [selected, options]
    );

    const handleReset = useCallback(
      (e: any) => {
        e.stopPropagation();
        onSelect && onSelect('');
      },
      [onSelect]
    );

    return (
      <FieldStructure error={error} hideErrorMessage={hideErrorMessage}>
        <ScSelectBlock ref={forwardedRef} active={isComponentVisible}>
          <SelectButton
            ref={openerRef}
            {...scProps}
            disabled={disabled}
            error={error}
            active={isComponentVisible}
            selectedOption={selectedOption}
            placeholder={placeholder}
            animatedPlaceholder={animatedPlaceholder}
            handleOpen={handleOpen}
            handleReset={handleReset}
            selected={selected}
            canReset={canReset}
            noSelectioinIfOneOption={
              noSelectioinIfOneOption && options.length < 2
            }
          />
          {isComponentVisible && (
            <Responsive
              desktopL={
                <OptionsBlock
                  ref={ref}
                  {...scProps}
                  selectedOption={selectedOption}
                  options={options}
                  onChange={(value) => {
                    onSelect && onSelect(value);
                    handleOpen();
                  }}
                  withBorderMode={withBorderMode}
                  search={search}
                />
              }
              desktop={MediaType.DESKTOP_L}
              tablet={MediaType.DESKTOP_L}
              mobile={MediaType.MOBILE_S}
              mobileS={
                <ModalSelect
                  title={placeholder || animatedPlaceholder || 'Select'}
                  options={options}
                  onChange={(value) => {
                    onSelect && onSelect(value);
                    handleOpen();
                  }}
                  onClose={handleOpen}
                  borderRoundIconOption
                  search={search}
                  selectedOption={selectedOption?.value}
                  fullHeight
                />
              }
            />
          )}
        </ScSelectBlock>
      </FieldStructure>
    );
  }
);

export const SelectButton = forwardRef<HTMLDivElement, SelectButtonProps>(
  (
    {
      selectedOption,
      handleOpen,
      handleReset,
      placeholder,
      animatedPlaceholder,
      selected,
      error,
      hideErrorMessage,
      disabled,
      defaultValue,
      canReset,
      withBorderMode,
      selectedOptionWithoutIcon,
      active,
      noSelectioinIfOneOption,
      ...scProps
    },
    ref
  ) => {
    const { arrowSelectIcon: ArrowSelectIcon } = useTheme();
    return (
      <ScSelectButton
        ref={ref}
        {...scProps}
        onClick={handleOpen}
        selected={!!selected}
        canReset={canReset}
        disabled={disabled}
        active={!!active}
        error={error != null && error !== ''}
        hasArrow={!noSelectioinIfOneOption}
      >
        <div className="border" />
        <div className="content">
          <div
            className={`value ${
              selectedOption?.icon != null && !selectedOptionWithoutIcon
                ? `with-icon`
                : ''
            } ${
              animatedPlaceholder != null &&
              (selectedOption != null || defaultValue != null)
                ? 'filled'
                : ''
            }`}
          >
            {!selectedOptionWithoutIcon && selectedOption?.icon != null && (
              <img src={selectedOption.icon} alt="" />
            )}
            {(selectedOption || defaultValue) && (
              <div className="name">
                {selectedOption == null
                  ? defaultValue
                  : selectedOption.nameV ?? selectedOption.name}
              </div>
            )}
            {selectedOption == null &&
              defaultValue == null &&
              placeholder != null && (
                <span className="placeholder">{capitalize(placeholder)}</span>
              )}
          </div>
          {animatedPlaceholder && (
            <ScAnimatedPlaceholder
              active={!!active}
              className={
                selectedOption != null || defaultValue != null ? 'filled' : ''
              }
            >
              {capitalize(animatedPlaceholder)}
            </ScAnimatedPlaceholder>
          )}
          {!noSelectioinIfOneOption && (
            <div className="arrow">
              {selected && canReset ? (
                <ButtonCross onClick={handleReset} />
              ) : (
                <>
                  <div className="bg-layer" />
                  <ArrowSelectIcon />
                </>
              )}
            </div>
          )}
        </div>
      </ScSelectButton>
    );
  }
);

interface OptionsBlockProps extends ScOptionsProps {
  options: SelectOption[];
  selectedOption?: SelectOption;
  onChange: (value: SelectOption['value']) => void;
  search?: boolean;
  addons?: JSX.Element | null;
  hideEmptyText?: boolean;
  minOptionHeight?: number;
}

const MIN_OPTIONS_LIST_HEIGHT = 208;
const MIN_OPTION_HEIGHT = 32;
const OPTIONS_LIST_GAPS = 8;

export const OptionsBlock = forwardRef<HTMLDivElement, OptionsBlockProps>(
  (
    {
      options,
      selectedOption,
      onChange,
      search,
      maxHeight,
      addons,
      hideEmptyText,
      minOptionHeight = MIN_OPTION_HEIGHT,
      ...props
    },
    forwardedRef
  ) => {
    const [searchValue, setSearchValue] = useState('');

    const ref = useRef<HTMLInputElement>(null);

    useEffect(() => {
      if (ref.current == null || search == null) return;

      ref.current.focus();
    }, [search]);

    const handleChangeSearchValue = useCallback(
      (e: React.ChangeEvent<HTMLInputElement>) => {
        setSearchValue(e.target.value);
      },
      [setSearchValue]
    );

    const filteredOptions = useMemo(() => {
      if (searchValue === '' || search == null) return options;

      return options.filter(({ nameS }) => {
        if (nameS == null) return true;
        return nameS.toLowerCase().includes(searchValue.toLowerCase());
      });
    }, [searchValue, options, search]);

    return (
      <ScOptions {...props} ref={forwardedRef}>
        {props.withBorderMode && <div className="border" />}

        <div className="content">
          {search && (
            <ScSelectSearch>
              <input
                ref={ref}
                type="text"
                placeholder="Search..."
                onChange={handleChangeSearchValue}
              />
              <div className="divider" />
            </ScSelectSearch>
          )}
          {filteredOptions.length === 0 ? (
            !hideEmptyText ? (
              <ul className="options-list">
                <li className="not-found">Not found</li>
              </ul>
            ) : null
          ) : (
            <ScrollBlock
              style={{
                height:
                  typeof maxHeight === 'number'
                    ? `${maxHeight}px`
                    : filteredOptions.length >= 6
                    ? `${MIN_OPTIONS_LIST_HEIGHT}px`
                    : `${
                        filteredOptions.length * minOptionHeight +
                        OPTIONS_LIST_GAPS
                      }px`,
                width: '100%',
              }}
            >
              <ul
                className={`options-list ${
                  filteredOptions.length * minOptionHeight <
                  (Number(maxHeight) || MIN_OPTIONS_LIST_HEIGHT)
                    ? 'no-scroll'
                    : ''
                }`}
              >
                {filteredOptions.map(({ icon, name, value }, index) => (
                  <li
                    key={index}
                    className={`${
                      selectedOption?.value === value && 'active'
                    } ${icon != null && 'with-icon'}`}
                    onClick={() => onChange(value)}
                  >
                    {icon && <img src={icon} alt={`${name}`} />}
                    <div className="name">{name}</div>
                  </li>
                ))}
              </ul>
            </ScrollBlock>
          )}
          {addons && <div className="addons">{addons}</div>}
        </div>
      </ScOptions>
    );
  }
);
